import {useEffect, useMemo, useState} from 'react';
import {useIntl} from 'react-intl';
import useAlerts from 'src/hooks/use-alerts';
import {MAX_DEVICE_IMAGES} from './constants';
import {ErrorCode} from 'react-dropzone';
import {v4 as uuid} from 'uuid';

const useCreateDeviceViewModel = (device = null) => {
    const {toast} = useAlerts();
    const intl = useIntl();

    const [images, setImages] = useState([]);
    const [imageEditionLocked, setImageEditionLock] = useState(
        device !== null ? true : false,
    );
    const [loadingDeviceImages, setLoadingDeviceImages] = useState(device !== null ? true : false);
    const imagesAmount = images.length;

    const imagesWithPreview = useMemo(() => {
        return images.map((image) => {
            if (image.id) return image;
            Object.assign(image, {
                id: uuid(),
                preview: URL.createObjectURL(image),
            });
            return image;
        });
    }, [images]);

    const addImage = (imgs) => {
        const deviceImagesAmount = imagesAmount;
        const newDeviceImagesAmount = imgs.length;

        if (deviceImagesAmount + newDeviceImagesAmount > MAX_DEVICE_IMAGES) {
            return toast.error(intl.formatMessage({id: ErrorCode.TooManyFiles}, {
                max_allowed_files: MAX_DEVICE_IMAGES,
            }));
        }

        setImages((prev) => [...prev, ...imgs]);
    };

    const removeImage = (img) => () => {
        setImages((prev) => prev.filter((item) => item !== img));
    };

    const pinImage = (img) => () => {
        setImages((prev) => {
            const imageIndex = prev.findIndex((image) => image === img);
            if (imageIndex === -1) return prev;

            const newImages = [...prev];
            const [newPinned] = newImages.splice(imageIndex, 1);
            newImages.splice(0, 0, newPinned);

            return newImages;
        });
    };

    const unlockImageEdition = () => {
        setImages([]);
        setImageEditionLock(false);
    };

    useEffect(() => {
        // Make sure to revoke the data uris to avoid memory leaks, will run on unmount
        return () => {
            if (!device) {
                imagesWithPreview
                    .forEach((image) => URL.revokeObjectURL(image.preview));
            }
        };
    }, [imagesWithPreview]);

    useEffect(() => {
        if (device) {
            device.findImages().then((_images) => {
                if (_images.length === 0) {
                    setImageEditionLock(false);
                } else {
                    setImages(_images.map((image) => {
                        const nameParts = image.split('/');
                        const nameAlias = nameParts[nameParts.length - 1];

                        return {
                            id: uuid(),
                            preview: image,
                            name: nameAlias,
                        };
                    }));
                }
                setLoadingDeviceImages(false);
            });
        }
    }, [device]);

    return {
        images: imagesWithPreview,
        imagesAmount,
        imageEditionLocked,
        loadingDeviceImages,
        addImage,
        removeImage,
        pinImage,
        unlockImageEdition,
    };
};

export default useCreateDeviceViewModel;
